import { useState } from "react";

export default function FMContact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      await fetch("https://readdy.ai/api/form/d7d204hnoju8nk5a137g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="fm-contact"
      className="py-24 px-6"
      style={{ background: "linear-gradient(135deg, #fffbf5 0%, #fff8ed 100%)" }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: copy */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)" }}
            >
              <i className="ri-mail-send-line text-xs" style={{ color: "#D97706" }} />
              <span
                className="text-xs font-semibold tracking-widest uppercase"
                style={{ color: "#D97706", letterSpacing: "0.2em" }}
              >
                Get a Custom Quote
              </span>
            </div>

            <h2
              className="font-black mb-6"
              style={{ fontSize: "clamp(2rem, 3.5vw, 2.8rem)", color: "#1a1a1a", lineHeight: 1.15 }}
            >
              Tell Us What
              <br />
              <span style={{ color: "#D97706" }}>You Need Cast.</span>
            </h2>

            <p
              className="text-base font-light leading-relaxed mb-8"
              style={{ color: "rgba(30,20,5,0.6)", lineHeight: "1.85" }}
            >
              Whether you have a worn engine block sample, a CAD file, or just a part number —
              our engineering team will assess feasibility and provide a detailed quote within
              <strong style={{ color: "#1a1a1a" }}> 24 business hours</strong>.
            </p>

            {/* What to include */}
            <div
              className="rounded-2xl p-6 mb-8"
              style={{ background: "rgba(255,255,255,0.8)", border: "1px solid rgba(217,119,6,0.18)" }}
            >
              <p
                className="text-xs font-semibold tracking-widest uppercase mb-4"
                style={{ color: "rgba(30,20,5,0.4)", letterSpacing: "0.18em" }}
              >
                What to Include in Your Inquiry
              </p>
              {[
                { icon: "ri-car-line", text: "Engine model / application (e.g. Renault K4M, Cummins ISF)" },
                { icon: "ri-stack-line", text: "Required quantity (MOQ, annual volume)" },
                { icon: "ri-map-pin-2-line", text: "Destination port / country" },
                { icon: "ri-file-text-line", text: "Any available drawings, samples, or OEM part numbers" },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3 mb-3 last:mb-0">
                  <div
                    className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0 mt-0.5"
                    style={{ background: "rgba(217,119,6,0.1)" }}
                  >
                    <i className={`${item.icon} text-xs`} style={{ color: "#D97706" }} />
                  </div>
                  <p className="text-sm font-light" style={{ color: "rgba(30,20,5,0.65)", lineHeight: "1.7" }}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Contact info */}
            <div className="flex flex-col gap-3">
              <a
                href="mailto:chirunnexa@gmail.com"
                className="flex items-center gap-3 text-sm font-medium cursor-pointer transition-colors duration-200"
                style={{ color: "rgba(30,20,5,0.6)", textDecoration: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.6)")}
              >
                <i className="ri-mail-line" style={{ color: "#D97706" }} />
                chirunnexa@gmail.com
              </a>
              <p className="flex items-center gap-3 text-sm font-light" style={{ color: "rgba(30,20,5,0.45)" }}>
                <i className="ri-time-line" style={{ color: "#D97706" }} />
                Response within 24 business hours
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div
            className="rounded-2xl p-8"
            style={{ background: "#ffffff", border: "1px solid rgba(217,119,6,0.18)" }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div
                  className="w-16 h-16 flex items-center justify-center rounded-full mb-6"
                  style={{ background: "rgba(217,119,6,0.1)" }}
                >
                  <i className="ri-check-line text-2xl" style={{ color: "#D97706" }} />
                </div>
                <h3 className="font-black text-xl mb-3" style={{ color: "#1a1a1a" }}>
                  Quote Request Received
                </h3>
                <p className="text-sm font-light" style={{ color: "rgba(30,20,5,0.55)", lineHeight: "1.8" }}>
                  Thank you for your inquiry. Our engineering team will review your
                  requirements and respond within 24 business hours.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="foundry-custom-quote"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold tracking-wide" style={{ color: "rgba(30,20,5,0.55)" }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="John Smith"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                      style={{
                        background: "rgba(255,248,235,0.6)",
                        border: "1.5px solid rgba(217,119,6,0.2)",
                        color: "#1a1a1a",
                        fontSize: "14px",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold tracking-wide" style={{ color: "rgba(30,20,5,0.55)" }}>
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      required
                      placeholder="Your Company Ltd."
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                      style={{
                        background: "rgba(255,248,235,0.6)",
                        border: "1.5px solid rgba(217,119,6,0.2)",
                        color: "#1a1a1a",
                        fontSize: "14px",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold tracking-wide" style={{ color: "rgba(30,20,5,0.55)" }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@company.com"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                    style={{
                      background: "rgba(255,248,235,0.6)",
                      border: "1.5px solid rgba(217,119,6,0.2)",
                      color: "#1a1a1a",
                      fontSize: "14px",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold tracking-wide" style={{ color: "rgba(30,20,5,0.55)" }}>
                      Engine Model / Application
                    </label>
                    <input
                      type="text"
                      name="engine_model"
                      placeholder="e.g. Renault K4M, Cummins ISF"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                      style={{
                        background: "rgba(255,248,235,0.6)",
                        border: "1.5px solid rgba(217,119,6,0.2)",
                        color: "#1a1a1a",
                        fontSize: "14px",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold tracking-wide" style={{ color: "rgba(30,20,5,0.55)" }}>
                      Required Quantity
                    </label>
                    <input
                      type="text"
                      name="quantity"
                      placeholder="e.g. 50 pcs / month"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                      style={{
                        background: "rgba(255,248,235,0.6)",
                        border: "1.5px solid rgba(217,119,6,0.2)",
                        color: "#1a1a1a",
                        fontSize: "14px",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold tracking-wide" style={{ color: "rgba(30,20,5,0.55)" }}>
                    Destination Country / Port
                  </label>
                  <input
                    type="text"
                    name="destination"
                    placeholder="e.g. Russia, Vladivostok / UAE, Jebel Ali"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                    style={{
                      background: "rgba(255,248,235,0.6)",
                      border: "1.5px solid rgba(217,119,6,0.2)",
                      color: "#1a1a1a",
                      fontSize: "14px",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold tracking-wide flex items-center justify-between" style={{ color: "rgba(30,20,5,0.55)" }}>
                    <span>Project Details &amp; Requirements</span>
                    <span style={{ color: charCount > 500 ? "#ef4444" : "rgba(30,20,5,0.35)", fontSize: "11px" }}>
                      {charCount}/500
                    </span>
                  </label>
                  <textarea
                    name="project_details"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your engine block requirements, available samples, drawings, OEM part numbers, or any special specifications..."
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 resize-none"
                    style={{
                      background: "rgba(255,248,235,0.6)",
                      border: "1.5px solid rgba(217,119,6,0.2)",
                      color: "#1a1a1a",
                      fontSize: "14px",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
                    onChange={(e) => setCharCount(e.target.value.length)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold tracking-widest text-sm text-white transition-all duration-200 hover:brightness-110 active:scale-95 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                    letterSpacing: "0.13em",
                  }}
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      Request a Custom Quote
                    </>
                  )}
                </button>

                <p className="text-xs text-center" style={{ color: "rgba(30,20,5,0.35)" }}>
                  No commitment required · Response within 24 business hours
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
